import { format } from 'date-fns';
import { Lock, Archive } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { JournalEntryWithAuthor, JournalLockRow } from '@/hooks/useJournal';
import { cn } from '@/utils/cn';

function wasUpdated(entry: JournalEntryWithAuthor): boolean {
  return new Date(entry.updated_at).getTime() > new Date(entry.created_at).getTime() + 1000;
}

interface JournalEntryListItemProps {
  entry: JournalEntryWithAuthor;
  locks: JournalLockRow[];
  currentUserId: string | null | undefined;
  onClick: () => void;
  compact?: boolean;
}

export function JournalEntryListItem({
  entry,
  locks,
  currentUserId,
  onClick,
  compact = false,
}: JournalEntryListItemProps) {
  const { t } = useTranslation();
  const lock = locks.find((l) => l.entry_id === entry.id);
  const isLockedByOther = lock && lock.user_id !== currentUserId;
  const isLockedByMe = lock && lock.user_id === currentUserId;

  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full rounded-md border bg-white p-2 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-500 dark:hover:bg-gray-750',
          entry.archived_at && 'opacity-60',
        )}
      >
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-gray-900 dark:text-white">
              {entry.title || t('journal.untitled')}
            </p>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {format(new Date(entry.entry_date), 'P')}
            </p>
          </div>
          {(isLockedByMe || isLockedByOther || entry.archived_at) && (
            <div className="shrink-0">
              {entry.archived_at && <Archive className="h-3 w-3 text-gray-400" />}
              {isLockedByMe && <Lock className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />}
              {isLockedByOther && <Lock className="h-3 w-3 text-amber-600 dark:text-amber-400" />}
            </div>
          )}
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border bg-white p-4 text-left shadow-sm transition-colors hover:border-indigo-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-500',
        entry.archived_at && 'opacity-60',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
            {entry.title || t('journal.untitled')}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {format(new Date(entry.entry_date), 'PP')}
            {' · '}
            {t(`journal.type_${entry.entry_type}`)}
          </p>
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            {entry.author_name
              ? t('journal.created_by', {
                  name: entry.author_name,
                  date: format(new Date(entry.created_at), 'PPp'),
                })
              : t('journal.created_at', {
                  date: format(new Date(entry.created_at), 'PPp'),
                })}
            {wasUpdated(entry) && (
              <>
                {' · '}
                {t('journal.updated_at', {
                  date: format(new Date(entry.updated_at), 'PPp'),
                })}
              </>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {entry.archived_at && (
            <span
              title={t('journal.archived')}
              className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400"
            >
              <Archive className="h-3 w-3" />
              {t('journal.archived')}
            </span>
          )}
          {isLockedByMe && (
            <span
              title={t('journal.lock_held_by_you')}
              className="flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
            >
              <Lock className="h-3 w-3" />
              {t('journal.editing')}
            </span>
          )}
          {isLockedByOther && (
            <span
              title={t('journal.lock_held_by', { name: lock.user_name_snapshot })}
              className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
            >
              <Lock className="h-3 w-3" />
              {lock.user_name_snapshot}
            </span>
          )}
        </div>
      </div>
      {entry.content_md && (
        <p className="mt-2 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">
          {entry.content_md.replace(/[#*_`]/g, '').slice(0, 120)}
        </p>
      )}
    </button>
  );
}
